import './i18n';
import { useEffect } from 'react';
import { initSentry } from '@/lib/sentry';
import { initAnalytics } from '@/lib/analytics';

initSentry();
initAnalytics().catch((err) => {
  console.error('[analytics] Failed to initialize:', err);
  // Forward to Sentry if available so init failures are observable in prod.
  import('@/lib/sentry')
    .then(({ Sentry, isSentryEnabled }) => {
      if (isSentryEnabled()) Sentry.captureException(err);
    })
    .catch(() => {
      /* Sentry not configured — console.error above is the only signal */
    });
});

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { BrandingProvider } from '@/components/branding/BrandingProvider';
import { AgentBridgeProvider } from '@/agent/AgentBridgeProvider';
import { MfaGate } from '@/components/auth/MfaGate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useHeartbeat } from '@/hooks/useHeartbeat';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { startDaySimulator } from '@/sim/DaySimulator';
import { AppRouter } from '@/router';
import { CommandPalette } from '@/components/CommandPalette';
import { KeyboardShortcutsPanel } from '@/components/KeyboardShortcutsPanel';
import { OnboardingTour } from '@/components/OnboardingTour';

const queryClient = new QueryClient();

function SimulationRoot() {
  useHeartbeat();
  useRealtimeNotifications();
  useEffect(() => {
    startDaySimulator();
  }, []);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SimulationRoot />
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ErrorBoundary>
        <BrowserRouter>
          {/*
           * Provider order is INTENTIONAL — do not reorder (BUG-8).
           *
           *   AuthProvider        → exposes the authenticated Supabase user.
           *   └─ TenantProvider   → depends on `useAuth().user` to load
           *                         tenant memberships from `tenant_users`.
           *      └─ BrandingProvider → reads `useTenant().currentOrg` to
           *                            apply per-tenant theming.
           *         └─ AgentBridgeProvider → needs auth + tenant context for
           *                                  scoped agent actions.
           *
           * Inverting Auth/Tenant would break tenant resolution because
           * TenantProvider would mount before a user is known.
           */}
          <AuthProvider>
            <TenantProvider>
              <BrandingProvider>
                <AgentBridgeProvider>
                  <MfaGate>
                    <AppRouter />
                  </MfaGate>
                  <CommandPalette />
                  <KeyboardShortcutsPanel />
                  <OnboardingTour />
                </AgentBridgeProvider>
              </BrandingProvider>
            </TenantProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
